import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Wrench, Truck, Palette, Store, Factory, HardHat } from "lucide-react";

interface BusinessDetailsFormProps {
  isEditing: boolean;
  businessName: string;
  businessPhone: string;
  businessWebsite: string;
  businessDescription: string;
  categories: string[];
  onBusinessNameChange: (value: string) => void;
  onBusinessPhoneChange: (value: string) => void;
  onBusinessWebsiteChange: (value: string) => void;
  onBusinessDescriptionChange: (value: string) => void;
  onCategoriesChange: (value: string[]) => void;
}

const VALID_CATEGORIES = [
  { name: 'installer', Icon: Wrench },
  { name: 'supplier', Icon: Truck },
  { name: 'designer', Icon: Palette },
  { name: 'retailer', Icon: Store },
  { name: 'manufacturer', Icon: Factory },
  { name: 'contractor', Icon: HardHat }
];

const BusinessDetailsForm = ({
  isEditing,
  businessName,
  businessPhone,
  businessWebsite,
  businessDescription,
  categories: selectedCategories = [],
  onBusinessNameChange,
  onBusinessPhoneChange,
  onBusinessWebsiteChange,
  onBusinessDescriptionChange,
  onCategoriesChange,
}: BusinessDetailsFormProps) => {
  const handleCategoryToggle = (categoryName: string) => {
    const updatedCategories = selectedCategories.includes(categoryName)
      ? selectedCategories.filter(cat => cat !== categoryName)
      : [...selectedCategories, categoryName];
    onCategoriesChange(updatedCategories);
  };

  if (!isEditing) {
    return (
      <div className="space-y-4">
        <div>
          <Label className="text-gray-700">Business Name</Label>
          <p className="text-gray-600">{businessName || 'Not set'}</p>
        </div>
        <div>
          <Label className="text-gray-700">Categories</Label>
          <div className="flex flex-wrap gap-2">
            {selectedCategories.map(category => (
              <span
                key={category}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-sm"
              >
                {category}
              </span>
            ))}
            {selectedCategories.length === 0 && (
              <p className="text-gray-600">No categories selected</p>
            )}
          </div>
        </div>
        <div>
          <Label className="text-gray-700">Business Phone</Label>
          <p className="text-gray-600">{businessPhone || 'Not set'}</p>
        </div>
        <div>
          <Label className="text-gray-700">Business Website</Label>
          <p className="text-gray-600">{businessWebsite || 'Not set'}</p>
        </div>
        <div>
          <Label className="text-gray-700">Business Description</Label>
          <p className="text-gray-600">{businessDescription || 'Not set'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 bg-white/80 backdrop-blur-sm p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900">Business Details</h3>
      
      <div className="space-y-2">
        <Label htmlFor="businessName">Business Name</Label>
        <Input
          id="businessName"
          value={businessName}
          onChange={(e) => onBusinessNameChange(e.target.value)}
          placeholder="Enter your business name"
        />
      </div>

      <div className="space-y-2">
        <Label>Categories</Label>
        <ScrollArea className="h-[200px] w-full rounded-md border p-4">
          <div className="space-y-4">
            {VALID_CATEGORIES.map(({ name, Icon }) => (
              <div
                key={name}
                className="flex items-center space-x-3"
              >
                <Checkbox
                  id={`category-${name}`}
                  checked={selectedCategories.includes(name)}
                  onCheckedChange={() => handleCategoryToggle(name)}
                />
                <label
                  htmlFor={`category-${name}`}
                  className="flex items-center gap-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  <Icon className="h-4 w-4 text-purple-500" />
                  {name}
                </label>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      <div className="space-y-2">
        <Label htmlFor="businessPhone">Business Phone</Label>
        <Input
          id="businessPhone"
          value={businessPhone}
          onChange={(e) => onBusinessPhoneChange(e.target.value)}
          placeholder="Enter your business phone"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="businessWebsite">Business Website</Label>
        <Input
          id="businessWebsite"
          value={businessWebsite}
          onChange={(e) => onBusinessWebsiteChange(e.target.value)}
          placeholder="Enter your business website"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="businessDescription">Business Description</Label>
        <Textarea
          id="businessDescription"
          value={businessDescription}
          onChange={(e) => onBusinessDescriptionChange(e.target.value)}
          placeholder="Enter a description of your business"
          rows={4}
        />
      </div>
    </div>
  );
};

export default BusinessDetailsForm;